import { useState } from 'react';
import { Checkmark, Close, Edit } from '@carbon/react/icons';
import { Button, TextArea } from '@carbon/react';
import type { NotesAccordionProps } from './types';
import styles from './notes-accordion.module.scss';

/**
 * Scenario-level notes section shown at the top of the scenario form. Renders a
 * plain full-width card (no collapse) with the current notes or a placeholder;
 * the edit pencil reveals a textarea with save/cancel actions. Save commits the
 * draft, cancel discards it.
 * @param {NotesAccordionProps} props - The component props
 * @return {JSX.Element} The notes card
 */
export function NotesAccordion({
  value,
  onChange,
  placeholder = 'Click to add notes...',
}: NotesAccordionProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const startEdit = (): void => {
    setDraft(value);
    setEditing(true);
  };

  const save = (): void => {
    onChange(draft);
    setEditing(false);
  };

  const cancel = (): void => {
    setDraft(value);
    setEditing(false);
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h4 className={styles.title}>Notes</h4>
        {editing ? (
          <div className={styles.actions}>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Checkmark}
              iconDescription="Save notes"
              tooltipPosition="bottom"
              onClick={save}
            />
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Close}
              iconDescription="Cancel"
              tooltipPosition="bottom"
              onClick={cancel}
            />
          </div>
        ) : (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Edit}
            iconDescription="Edit notes"
            tooltipPosition="bottom"
            onClick={startEdit}
          />
        )}
      </div>

      {editing ? (
        <TextArea
          id="scenario-notes"
          labelText=""
          hideLabel
          value={draft}
          placeholder="Add notes about this scenario..."
          rows={3}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setDraft(e.target.value)
          }
        />
      ) : (
        <p
          className={styles.preview}
          role="button"
          tabIndex={0}
          onClick={startEdit}
          onKeyDown={(e: React.KeyboardEvent<HTMLParagraphElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEdit();
            }
          }}
        >
          {value || placeholder}
        </p>
      )}
    </div>
  );
}
